  import React, { useState, useEffect } from "react";
  import {
    Layout,
    Button,
    Table,
    Space,
    Modal,
    Input,
    Form,
    Card,
    Typography,
    Popconfirm,
    Switch,
    Upload,
    Select,
    Image,
    message,
  } from "antd";

  import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    UploadOutlined,
    DragOutlined,
    FullscreenOutlined,
  } from "@ant-design/icons";

  import {
    createWork,
    getAllWorks,
    updateWorkSize,
    updatePublishStatus,
    deleteWork,
    getAllImageSizes,
    updatePriority,
    updateWorkDetails,
    updateWorkImage,
    updatePortfolioImage,
  } from "../services/work.service";

  import "./recentworks.css";
  import ImageGridSorter from "./ImageGridSorter";

  const { Content } = Layout;
  const { Title } = Typography;
  const { Option } = Select;

  const RecentWorks = () => {
    const [form] = Form.useForm();

    const [data, setData] = useState([]);
    const [imageSizes, setImageSizes] = useState([]);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    const [imageUrl, setImageUrl] = useState("");

    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewImage, setPreviewImage] = useState("");

    const [pagination, setPagination] = useState({
      current: 1,
      pageSize: 5,
      total: 0,
    });

    const [isOrderView, setIsOrderView] = useState(false);
    const [orderedData, setOrderedData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [editForm] = Form.useForm();
     const [editItem, setEditItem] = useState(null);
     const [uploadingId, setUploadingId] = useState(null);
     const [portfolioImageUrl, setPortfolioImageUrl] = useState("");
    // ================= FETCH WORKS =================

    const fetchWorks = async (page = 1, limit = 5) => {
      try {
        setLoading(true);

        const res = await getAllWorks(page, limit);

        setData(res?.data?.data || []);

        setPagination({
          current: page,
          pageSize: limit,
          total: res.data.pagination.totalItems,
        });
      } catch (error) {
        message.error("Failed to fetch works");
      } finally {
        setLoading(false);
      }
    };

    // ================= FETCH IMAGE SIZES =================

    const fetchImageSizes = async () => {
      try {
        const res = await getAllImageSizes();

        setImageSizes(res.data.data);
      } catch {
        message.error("Failed to load image sizes");
      }
    };

    useEffect(() => {
      fetchWorks(1, 5);
      fetchImageSizes();
    }, []);

    const openEditModal = (record) => {
  setEditItem(record);
  setIsEditModalVisible(true);

  editForm.setFieldsValue({
    topic: record.topic,
    title: record.title,
  });
};

    const handleEditUpdate = async (values) => {
  try {
    await updateWorkDetails(editItem.id, {
      topic: values.topic,
      title: values.title,
    });

    message.success("Updated successfully");

    setIsEditModalVisible(false);
    fetchWorks(pagination.current, pagination.pageSize);
  } catch {
    message.error("Update failed");
  }
};

const handleImageUpdate = async (file, record) => {
  const reader = new FileReader();

  reader.onload = async () => {
    try {
      setUploadingId(record.id);

      await updateWorkImage(record.id, {
        workImage: reader.result,
      });

      message.success("Image updated successfully");

      fetchWorks(pagination.current, pagination.pageSize);
    } catch {
      message.error("Image update failed");
    } finally {
      setUploadingId(null);
    }
  };

  reader.readAsDataURL(file);

  return false;
};
    // ================= MODAL =================

    const showModal = (item = null) => {
      setEditingItem(item);
      setIsModalVisible(true);

      if (item) {
        form.setFieldsValue({
          size: `${item.widthSize}-${item.heightSize}`,
        });
      } else {
        form.resetFields();
        setImageUrl("");
      }
    };

    // ================= CREATE =================

    const handleCreate = async (values) => {
      try {
        if (!imageUrl) {
          message.error("Please upload image");
          return;
        }

        const [widthSize, heightSize] = values.size.split("-");

        await createWork({
          topic: values.topic,
          title: values.title,
          widthSize,
          heightSize,
          workImage: imageUrl,
          isPublish: values.isPublish ? 1 : 0,
          portfolioImage: values.portfolioImage,
        });

        message.success("Work created successfully");

        setIsModalVisible(false);

        fetchWorks(1, 5);
      } catch {
        message.error("Create failed");
      }
    };

    const handlePortfolioUpdate = async (file, record) => {
  const reader = new FileReader();

  reader.onload = async () => {
    try {
      setUploadingId(record.id);

      await updatePortfolioImage(record.id, {
        portfolioImage: reader.result,
      });

      message.success("Portfolio Image updated");

      fetchWorks(pagination.current, pagination.pageSize);
    } catch {
      message.error("Update failed");
    } finally {
      setUploadingId(null);
    }
  };

  reader.readAsDataURL(file);

  return false;
};
    // ================= UPDATE =================

    const handleUpdate = async (values) => {
      try {
        const [widthSize, heightSize] = values.size.split("-");

        await updateWorkSize(editingItem.id, {
          widthSize,
          heightSize,
        });

        message.success("Size updated successfully");

        setIsModalVisible(false);

        fetchWorks(pagination.current, pagination.pageSize);
      } catch {
        message.error("Update failed");
      }
    };

    // ================= PUBLISH =================

    const togglePublish = async (id, checked) => {
      await updatePublishStatus(id, {
        isPublish: Number(checked),
      });

      message.success(
        checked ? "Published Successfully" : "Unpublished Successfully",
      );

      fetchWorks(pagination.current, pagination.pageSize);
    };

    // ================= DELETE =================

    const handleDelete = async (id) => {
      await deleteWork(id);

      message.success("Deleted successfully");

      fetchWorks(pagination.current, pagination.pageSize);
    };

    const handleSaveOrder = async () => {
      try {
        const reorderedItems = orderedData.map((item, index) => ({
          id: item.id,
          priority: index,
        }));

        await updatePriority({
          reorderedItems,
        });

        message.success("Order updated successfully");

      } catch (error) {
        message.error("Failed to update order");
      }
    };
    // ================= TABLE =================

    const columns = [
      {
        title: "Image",
        dataIndex: "workImage",
       render: (img, record) => (
  <div style={{ textAlign: "center" }}>
    <Image
      width={70}
      src={img}
      preview={false}
       style={{ cursor: "pointer" }}
      fallback="https://via.placeholder.com/70"
      onClick={() => {
        setPreviewImage(img);
        setPreviewVisible(true);
      }}
    />
<br></br><br></br>
    {/* UPDATE BUTTON */}
    <Upload
      showUploadList={false}
      beforeUpload={(file) => handleImageUpdate(file, record)}
    >
      <Button
        size="small"
        style={{ marginTop: 5 }}
        loading={uploadingId === record.id}
        icon={<UploadOutlined />}
      >
        Update
      </Button>
    </Upload>
  </div>
)
      },
      {
  title: "Portfolio Image",
  dataIndex: "portfolioImage",
  render: (img, record) => (
  <div style={{ textAlign: "center" }}>
    {img ? (
      <Image
        src={img}
        width={60}
        height={60}
         style={{ cursor: "pointer" }}
        preview={false}
        fallback="https://via.placeholder.com/70"
        onClick={() => {
          setPreviewImage(img);
          setPreviewVisible(true);
        }}
      />
    ) : (
      "No Image"
    )}
<br></br><br></br>
    {/* 🔥 UPDATE BUTTON */}
    <Upload
      showUploadList={false}
      beforeUpload={(file) => handlePortfolioUpdate(file, record)}
    >
      <Button
        size="small"
        style={{ marginTop: 5 }}
        loading={uploadingId === record.id}
        icon={<UploadOutlined />} 
      >
        Update
      </Button>
    </Upload>
  </div>
)
},

      { title: "Topic", dataIndex: "topic" },

      { title: "Title", dataIndex: "title" },

      {
        title: "Size",
        render: (_, record) => `${record.widthSize} x ${record.heightSize}`,
      },

      {
        title: "Publish",
        render: (_, record) => (
          <Switch
            checked={Number(record.isPublish) === 1}
            onChange={(checked) => togglePublish(record.id, checked)}
          />
        ),
      },

      {
        title: "Actions",
        render: (_, record) => (
          <Space direction="vertical">
            <Button icon={<FullscreenOutlined />} onClick={() => showModal(record)}>
              Size
            </Button>
            <Button
            icon={<EditOutlined />}
             onClick={() => openEditModal(record)}
             >
              Edit
            </Button>
            <Popconfirm
              title="Delete this image?"
              onConfirm={() => handleDelete(record.id)}
            >
              <Button danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ];

    return (
      <Layout className="admin-layout">
        <Content className="admin-content">
          <Card title={<Title level={4}>Recent Works</Title>}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => showModal()}
            >
              Add Work
            </Button>

            <Button
              style={{ marginLeft: 20 }}
              icon={<DragOutlined />}
              onClick={async () => {
                if (!isOrderView) {
                  // fetch ALL works when reorder opens
                  const res = await getAllWorks(1, 1000);

                  const allData = res?.data?.data || [];

                  setData(allData);

                  setOrderedData(
                    allData.map((i) => ({
                      id: i.id,
                      image: i.workImage,
                      title: i.title,
                      subtitle: i.topic,
                    })),
                  );
                }

                setIsOrderView(!isOrderView);
              }}
            >
              {isOrderView ? "Back to List" : "Reorder"}
            </Button>
            {isOrderView && (
              <Button
                type="primary"
                style={{ marginTop: 20, marginLeft: 10 }}
                onClick={handleSaveOrder}
              >
                Save Order
              </Button>
            )}
            <br />
            <br />

            {isOrderView && (
              <ImageGridSorter
                data={data.map((i) => ({
                  id: i.id,
                  image: i.workImage,
                  title: i.title,
                  subtitle: i.topic,
                }))}
                onChange={(newOrder) => {
                  setOrderedData(newOrder);

                  const updated = newOrder.map((item) =>
                    data.find((d) => d.id === item.id),
                  );

                  setData(updated);
                }}
              />
            )}

            {!isOrderView && (
              <Table
                columns={columns}
                dataSource={data}
                rowKey="id"
                loading={loading}
                scroll={{ x: 700 }}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  showSizeChanger: false,
                }}
                onChange={(p) => fetchWorks(p.current, p.pageSize)}
              />
            )}
          </Card>
        </Content>

        {/* ================= MODAL ================= */}

        <Modal
          title={editingItem ? "Edit Size" : "Add Work"}
          open={isModalVisible}
          footer={null}
          onCancel={() => {
            setIsModalVisible(false);
            setEditingItem(null);
            form.resetFields();
            setImageUrl("");             
  setPortfolioImageUrl(""); 
          }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={editingItem ? handleUpdate : handleCreate}
          >
            {!editingItem && (
              <>
                <Form.Item label="Upload Image" required>
                  <Upload
                    showUploadList={false}
                    beforeUpload={(file) => {
                      const reader = new FileReader();

                      reader.onload = () => {
                        setImageUrl(reader.result);
                      };

                      reader.readAsDataURL(file);

                      return false;
                    }}
                  >
                    <Button icon={<UploadOutlined />}>Select Image</Button>
                  </Upload> <br></br>

                  {imageUrl && (
                    <Image src={imageUrl} width={100} style={{ marginTop: 10 }} />
                  )}
                </Form.Item>

              <Form.Item
  name="portfolioImage"
  label="Portfolio Image"
  rules={[{ required: true, message: "Please upload portfolio image" }]}
>
  <Upload
  showUploadList={false}
  beforeUpload={(file) => {
    const reader = new FileReader();

    reader.onload = () => {
      const base64 = reader.result;

      form.setFieldsValue({
        portfolioImage: base64,
      });

      setPortfolioImageUrl(base64); // 🔥 same logic
    };

    reader.readAsDataURL(file);

    return false;
  }}
>
  <Button icon={<UploadOutlined />}>
    Upload Portfolio Image
  </Button>
</Upload>
<br></br>
  {portfolioImageUrl && (
  <Image
    src={portfolioImageUrl}
    width={100}
    style={{ marginTop: 10 }}
  />
)}
</Form.Item>

                <Form.Item
                  name="topic"
                  label="Topic"
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>

                <Form.Item
                  name="title"
                  label="Title"
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>

                <Form.Item
                  name="isPublish"
                  label="Publish"
                  valuePropName="checked"
                  initialValue={true}
                >
                  <Switch />
                </Form.Item>
              </>
            )}

            <Form.Item
              name="size"
              label="Image Size"
              rules={[{ required: true }]}
            >
              <Select placeholder="Select Size">
                {imageSizes.map((size) => (
                  <Option
                    key={size.id}
                    value={`${size.widthSize}-${size.heightSize}`}
                  >
                    {size.title}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" block>
                {editingItem ? "Update" : "Save"}
              </Button>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
  title="Edit Details"
  open={isEditModalVisible}
  onCancel={() => setIsEditModalVisible(false)}
  footer={null}
>
  <Form
    form={editForm}
    layout="vertical"
    onFinish={handleEditUpdate}
  >
    <Form.Item
      name="topic"
      label="Topic"
      rules={[{ required: true, message: "Topic required" }]}
    >
      <Input />
    </Form.Item>

    <Form.Item
      name="title"
      label="Title"
      rules={[{ required: true, message: "Title required" }]}
    >
      <Input />
    </Form.Item>

    <Form.Item>
      <Button type="primary" htmlType="submit" block>
        Update
      </Button>
    </Form.Item>
  </Form>
</Modal>

        {/* ================= IMAGE PREVIEW ================= */}

        <Modal
          open={previewVisible}
          footer={null}
          centered
          onCancel={() => setPreviewVisible(false)}
          className="image-preview-modal"
        >
          <div className="image-preview-container">
            <img src={previewImage} alt="preview" />
          </div>
        </Modal>
      </Layout>
    );
  };

  export default RecentWorks;
